import { useCallback, useMemo, useState } from 'react';
import { useGetClientUsersQuery } from '@api/clientUsers';
import { Row } from '../../../../components/GenericTable/GenericTable';
import { mapClientUsersToTableRows } from '../utils/mapClientUsersToTableRows';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import { AdminClientUserDashboards } from '@api/types/clientUsers';

export type ClientTableRowType = 'company' | 'user';

export type ClientTableRow = {
  id: string;
  rowType: ClientTableRowType;
  company: string;
  user: string;
  role: CLIENT_USER_ROLE | null;
  dashboards: string;
  dashboardAccess?: AdminClientUserDashboards;
  totalEntitlementCount: number;
  hasFullDashboardAccess: boolean;
  joiningDate: string;
  status: string;
  clientCode?: string;
  membershipGrantId?: string;
  userId?: string;
};

type UseClientsTableParams = {
  pageSize: number;
  searchQuery: string;
  pageNumber: number;
  onPageReset?: () => void;
};

export const useClientsTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
}: UseClientsTableParams) => {
  const [sort, setSort] = useState('joiningDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading, isFetching, refetch } = useGetClientUsersQuery({
    pageNumber: pageNumber,
    pageSize,
    search: searchQuery,
    sort,
    sortOrder,
    role: roleFilter ? [roleFilter as CLIENT_USER_ROLE] : undefined,
    status: statusFilter ? [statusFilter] : undefined,
  });

  const rows = useMemo<Row<ClientTableRow>[]>(
    () => mapClientUsersToTableRows(data?.data.items ?? []),
    [data],
  );

  const total = data?.data.totalElements ?? 0;

  const handleSort = useCallback(
    (sortKey: string) => {
      if (sortKey === sort) {
        setSortOrder((previous) => (previous === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSort(sortKey);
        setSortOrder('ASC');
      }
      onPageReset?.();
    },
    [sort, onPageReset],
  );

  const handleRoleFilterChange = useCallback(
    (filter: string) => {
      setRoleFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleStatusFilterChange = useCallback(
    (filter: string) => {
      setStatusFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  return {
    rows,
    isLoading: isLoading || isFetching,
    total,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    statusFilter,
    handleStatusFilterChange,
    refetch,
  };
};
